// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Injectable({
  providedIn: "root",
})
export class ActivationsService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService
  ) {}

  /**
   * Create activation with given data
   *
   * @since 1.0.0
   *
   * @param data New activation setting data
   */
  create(data: object): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      "activations",
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get activations list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall(
      "GET",
      "activations",
      params,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get given activationId data from back-end
   *
   * @since 1.0.0
   *
   * @param activationId ID of the role to get
   */
  get(activationId: string): Observable<any> {
    return this.apiService
      .apiCall(
        "GET",
        `activations/${activationId}`,
        undefined,
        undefined,
        undefined,
        undefined,
        "v2"
      )
      .pipe(
        catchError((err) => {
          this.helperService.handleError(err);
          return throwError(err);
        })
      );
  }

  /**
   * Update activation with given activationId with given activation data
   *
   * @since 1.0.0
   *
   * @param activationId ID of role to update
   * @param data New activation's data
   */
  update(activationId: string, data: object): Observable<any> {
    return this.apiService.apiCall(
      "PUT",
      `activations/${activationId}`,
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Update activation with given activationId with given activation data
   *
   * @since 1.0.0
   *
   * @param activationId ID of role to update
   * @param data New activation's data
   */
  updateFields(activationId: string, data: object): Observable<any> {
    return this.apiService.apiCall(
      "PATCH",
      `activations/${activationId}`,
      data,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Delete permanently a activation
   *
   * @since 1.0.0
   *
   * @param activationId ID of the activation
   */
  delete(activationId: string): Observable<any> {
    return this.apiService.apiCall(
      "DELETE",
      `activations/${activationId}`,
      undefined,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }
}
